<template>
  <b-row
    v-if="loaded"
    align-h="center"
  >
    <b-col
      lg="12"
      xl="9"
      class="p-2"
    >
      <div class="item-text d-flex px-2 pb-2">
        <div class="d-flex align-items-center justify-content-center">
          <i
            :class="item.icon + ' mr-3 main'"
            aria-hidden="true"
          />
        </div>
        <div class="d-flex flex-column justify-content-start">
          <small>{{ $t('dashboard.widget.pendingCases') }}</small>
          <span>{{ $t(item.label) }}</span>
        </div>
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-case-situation-laboratories"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-hospital mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="situationChartLaboratoriesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="situationChartLaboratoriesSelected.length"
          target="pop-case-situation-laboratories"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedCenters') }}</strong></span>
            </div>
            <div
              v-for="(lab, index) in situationChartLaboratoriesSelected"
              :key="index"
            >
              {{ lab.labName }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="situationChartLaboratoriesSelected"
          :placeholder="$t('dashboard.widget.laboratory')"
          track-by="labId"
          :options="laboratories"
          label="labName"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshSituationChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-case-situation-case-types"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-chart-network mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="situationChartCaseTypesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="situationChartCaseTypesSelected.length"
          target="pop-case-situation-case-types"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedCaseTypes') }}</strong></span>
            </div>
            <div
              v-for="(caseType, index) in situationChartCaseTypesSelected"
              :key="index"
            >
              {{ caseType.description }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="situationChartCaseTypesSelected"
          :placeholder="$t('dashboard.widget.caseType')"
          track-by="id"
          :options="caseTypes"
          label="description"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshSituationChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-case-situation-priorities"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-flag mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="situationChartPrioritiesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="situationChartPrioritiesSelected.length"
          target="pop-case-situation-priorities"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedPriorities') }}</strong></span>
            </div>
            <div
              v-for="(lab, index) in situationChartPrioritiesSelected"
              :key="index"
            >
              {{ lab.name }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="situationChartPrioritiesSelected"
          :placeholder="$t('dashboard.widget.priority')"
          track-by="id"
          :options="priorities"
          label="name"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshSituationChartSelected($event)"
          @select="saveConfiguration"
        />
      </div>
      <div class="mt-3">
        <situation-chart
          v-if="item.subType === 'caseSituation' && caseTypes.length"
          ref="situationCaseChart"
          :laboratories-id="situationChartLaboratoriesSelected.length ? situationChartLaboratoriesSelected : laboratories"
          :priorities-id="situationChartPrioritiesSelected.length ? situationChartPrioritiesSelected : priorities"
          :casetypes-id="situationChartCaseTypesSelected.length ? situationChartCaseTypesSelected : caseTypes"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import SituationChart from '@/modules/dashboardModule/widgets/chartWidget/SituationChart'
import sessionStore from '@/session/sessionStore'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'
import { StoreUtils } from '@/shared/util/store-utils'

export default {
    name: 'ChartWidgetCaseSituation',
    components: {
        VtBubble,
        SituationChart
    },
    props: {
        item: { type: Object },
        caseTypes: { type: Array }
    },
    data() {
        return {
            situationChartLaboratoriesSelected: [],
            situationChartPrioritiesSelected: [],
            situationChartCaseTypesSelected: [],
            uiComponent: 'uicomponent.dashboard.diagnosis.casesituation',
            loaded: false
        }
    },
    computed: {
        priorities() {
            return sessionStore.state.priorities
        },
        laboratories() {
            return StoreUtils.laboratories
        }
    },
    async mounted() {
        const savedConfiguration = await customConfigResource.get(this.uiComponent)
        if (savedConfiguration && savedConfiguration.config) {
            this.situationChartLaboratoriesSelected = savedConfiguration.config.situationChartLaboratoriesSelected
            this.situationChartPrioritiesSelected = savedConfiguration.config.situationChartPrioritiesSelected
            this.situationChartCaseTypesSelected = savedConfiguration.config.situationChartCaseTypesSelected
        }
        this.loaded = true
    },
    methods: {
        refreshSituationChartSelected() {
            this.$nextTick(() => {
                this.$refs?.situationCaseChart?.fillData()
            })
        },
        saveConfiguration() {
            this.$nextTick(() => {
                const config = {
                    situationChartLaboratoriesSelected: this.situationChartLaboratoriesSelected,
                    situationChartPrioritiesSelected: this.situationChartPrioritiesSelected,
                    situationChartCaseTypesSelected: this.situationChartCaseTypesSelected
                }
                customConfigResource.save(this.uiComponent, config)
            })
        }
    }
}
</script>

<style scoped>

</style>
